/**
 * Coerce anything (Error, parsed JSON error body, string, Laravel validation
 * object) into a human-readable string for a toast / banner.
 *
 * Fixes the `[object Object]` toast: the backend returns errors in several
 * shapes, including a NESTED object e.g.
 *   { error: { message: "The given data was invalid.",
 *              details: { email: ["email non è valido."] } } }
 * Passing that straight into `new Error(obj)` or a toast stringifies it to
 * "[object Object]". We prefer the most specific field-level message
 * ("email non è valido.") and fall back through coarser messages.
 */

// Pull the first usable string out of a validation bag like
// { field: ["msg", ...] } or { field: "msg" }.
const firstFromBag = (bag) => {
  if (!bag || typeof bag !== "object") return null;
  for (const v of Object.values(bag)) {
    if (typeof v === "string" && v.trim()) return v.trim();
    if (Array.isArray(v)) {
      const s = v.find((m) => typeof m === "string" && m.trim());
      if (s) return s.trim();
    }
  }
  return null;
};

const fromObject = (node) => {
  if (!node || typeof node !== "object") return null;

  // 1. Field-level validation messages (most specific / user-friendly),
  // including the common one-level-nested { error: {...} } shape.
  const detail =
    firstFromBag(node.errors) ||
    firstFromBag(node.details) ||
    firstFromBag(node.error?.errors) ||
    firstFromBag(node.error?.details) ||
    firstFromBag(node.message?.details);
  if (detail) return detail;

  // 2. Plain human-readable message strings.
  const msg =
    (typeof node.message === "string" && node.message.trim()) ||
    (typeof node.error === "string" && node.error.trim()) ||
    (typeof node.error?.message === "string" && node.error.message.trim()) ||
    (typeof node.message?.message === "string" && node.message.message.trim());
  if (msg) return msg;

  return null;
};

export const toErrorText = (value, fallback = "Si è verificato un errore.") => {
  if (value == null) return fallback;

  if (typeof value === "string") return value.trim() || fallback;

  if (value instanceof Error) {
    const msg = value.message;
    // An Error built from an object has message === "[object Object]".
    if (typeof msg === "string" && msg && msg !== "[object Object]") return msg;
    return fallback;
  }

  if (typeof value === "object") {
    return fromObject(value) || fromObject(value.data) || fallback;
  }

  return fallback;
};

/**
 * What to say when the server answers "too many requests".
 *
 * The server's own wording is English ("Too Many Attempts."), which is fine for a
 * log and wrong for a member, so it is replaced rather than surfaced. Until the
 * backend was fixed to answer refusals with their real status a rate limit arrived
 * as a 500, which is why this case used to read as a generic failure — "Menu non
 * caricato", "Ops! C'è stato un errore" — with no hint that waiting would help.
 */
export const RATE_LIMITED_TEXT =
  "Troppe richieste in poco tempo. Attendi qualche istante e riprova.";

/** `toErrorText` for a response whose status is known. */
export const toResponseErrorText = (status, body, fallback) =>
  status === 429 ? RATE_LIMITED_TEXT : toErrorText(body, fallback);
